import { Alert, CircularProgress, IconButton, Snackbar, SxProps, Typography } from '@mui/material';
import useImageTransform from '../hooks/useImageGen';
import { useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import { AutoFixHigh as EnhanceIcon } from '@mui/icons-material';

const pre_defined_prompts = {
   prompt: 'transform the image to a cartoon style with a soft focus and different styles',
   style: 'realistic_image',
   substyle: '',
   strength: 0,
   num_images: 1,
};

const AIEnhanceImageButton = ({
   Icon,
   photoUrl,
   onDone,
   photoId,
   sx,
   checkSubscription,
}: {
   Icon?: React.ReactNode;
   photoUrl: string;
   onDone: (images: any[], photoId: string) => void;
   photoId: string;
   sx?: SxProps;
   checkSubscription?: () => boolean;
}) => {
   const { imageToImageEditing } = useImageTransform();
   const [isLoading, setIsLoading] = useState(false);


   const [snackbarOpen, setSnackbarOpen] = useState(false);
   const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
   const [snackbarMessage, setSnackbarMessage] = useState('');

   const handleCloseSnackbar = () => {
      setSnackbarOpen(false);
   };

   const enhanceImage = async () => {
      // Check subscription if checkSubscription function is provided
      if (checkSubscription && !checkSubscription()) {
         return; // Subscription modal will be shown by checkSubscriptionStatus
      }

      try {
         setIsLoading(true);
         const enhancedImage = await imageToImageEditing({
            ...pre_defined_prompts,
            url: photoUrl,
            template_type: 'enhance_photo',
         });

         if (enhancedImage) {
            onDone(enhancedImage.images || [], photoId);
         }
         setSnackbarSeverity('success');
         setSnackbarMessage('Image enhanced successfully');
         setSnackbarOpen(true);
      } catch (error) {
         setSnackbarSeverity('error');
         setSnackbarMessage('Error enhancing image');
         setSnackbarOpen(true);
      } finally {
         setIsLoading(false);
      }
   };

   return (
      <>
         <IconButton
            sx={{ ...sx, '&:hover': { backgroundColor: 'transparent' } }}
            disabled={isLoading}
            onClick={enhanceImage}
         >
            {isLoading ? (
               <CircularProgress color='secondary' size={20} />
            ) : Icon === undefined ? (
               <Tooltip title='Enhance Photo'>
                  <IconButton
                     size='small'
                     sx={{ width: 30, height: 30, backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                  >
                     <EnhanceIcon sx={{ fontSize: 20, color: 'secondary.main' }} />
                  </IconButton>
               </Tooltip>
            ) : (
               Icon
            )}
         </IconButton>
         <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
         >
            <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
               {snackbarMessage}
            </Alert>
         </Snackbar>
      </>
   );
};

export default AIEnhanceImageButton;
